import React from 'react';
import yaml from 'js-yaml';
// renderers is imported separately so Markdoc isn't sent to the client
import Markdoc, {renderers} from '@markdoc/markdoc'

import {getSchema, defaultObject} from '/vercel/path0/node_modules/@markdoc/next.js/src/runtime.js';
/**
 * Schema is imported like this so end-user's code is compiled using build-in babel/webpack configs.
 * This enables typescript/ESnext support
 */
const config = {};
import * as tags from '/vercel/path0/markdoc/tags/index.ts'
import * as nodes from '/vercel/path0/markdoc/nodes/index.ts'
const functions = {};
const schema = {
tags: defaultObject(tags),
nodes: defaultObject(nodes),
functions: defaultObject(functions),
...defaultObject(config),
};

const tokenizer = new Markdoc.Tokenizer();

/**
 * Source will never change at runtime, so parse happens at the file root
 */
const source = "---\ntitle: Glossary\npageTitle: Glossary | Covalent Network | Covalent\n---\n\n{% breadcrumb title=\"Glossary\" /%}\n\n# {% $markdoc.frontmatter.title %}\n\n### Covalent Network\n\nThe Covalent Network represents the progressive decentralization of Covalent toward a community-owned and community-run protocol.\n\nA multi-sided platform designed to eventually be self-sufficient, the Covalent Network works by capturing and indexing blockchain data and storing it across multiple points on the network. Anyone can then access this data instantly through querying the unified Covalent API.\n\n### Network Operators\n\nA collection of network participants who perform various roles in the network in extracting, transforming, tracing, indexing, delegating, governing, providing queries all the while making proofs-of-work done for receiving per block-rewards on a public blockchain.\n\n### Block Specimen Producer\n\nA blockchain client that asynchronously exports rich, cryptographically-secure data as block specimens.\n\n### Refiner\n\nThe refiner engine traces, decodes and enriches the block specimens into block results and aggregatable traces.\n\n### Query Operators\n\nThe subset of operators that host the decentralized database with the indexed blockchain data and a layer to extract insights from contract state snapshots allowing API calls to retrieve it.\n\n### Delegators\n\nThe subset of operators that delegate their CQT to stake within a pool of producers, auditors, refiners, indexers, queriers and receive a portion of the rewards of the work done by them proportional to their stake in the roles. They also participate in governance based on their stake in the evolution of functions undertaken by various roles in the network.\n\n### Auditors\n\nThe subset of operators from the pool that check the work of producers and prove when asked to - the work done by them on a public EVM chain.\n\n### Block Specimen\n\nThe Block Specimen is a bulk export method that can be implemented on existing blockchain clients including Geth and Erigon. These patches are essentially an extract-and-normalize worker that create Block Specimens, which together form a canonical representation of a blockchains full historical state.\n\n### Block Result\n\nA transformed Block Specimen that is organized in a schema the Covalent database expects.\n\n### ProofChain Contract\n\nThe ProofChain contract is a smart contract that 1) records information that meet specific validation rules and 2) determines consensus on network rewards by comparing these inputs.\n\nWith regard to the information recorded, these are proofs in the form of hashes. A proof is generated for every Block Specimen created by a Block Specimen Producer and submitted to the ProofChain contract, along with some other identifying information.\n";
const filepath = "/docs/covalent-network/glossary.md";
const tokens = tokenizer.tokenize(source);
const ast = Markdoc.parse(tokens);

/**
 * Like the AST, frontmatter won't change at runtime, so it is loaded at file root.
 * This unblocks future features, such a per-page dataFetchingFunction.
 */
const frontmatter = ast.attributes.frontmatter
  ? yaml.load(ast.attributes.frontmatter)
  : {};

const {components, ...rest} = getSchema(schema)

export async function getStaticProps(context) {
  const partials = {};

  // Ensure Node.transformChildren is available
  Object.keys(partials).forEach((key) => {
    const tokens = tokenizer.tokenize(partials[key]);
    partials[key] = Markdoc.parse(tokens);
  });

  const cfg = {
    ...rest,
    variables: {
      ...(rest ? rest.variables : {}),
      // user can't override this namespace
      markdoc: {frontmatter},
      // Allows users to eject from Markdoc rendering and pass in dynamic variables via getServerSideProps
      ...(context.variables || {})
    },
    partials,
    source,
  };

  /**
   * transform must be called in dataFetchingFunction to support server-side rendering while
   * accessing variables on the server
   */
  const content = await Markdoc.transform(ast, cfg);

  return {
    // Removes undefined
    props: JSON.parse(
      JSON.stringify({
        markdoc: {
          content,
          frontmatter,
          file: {
            path: filepath
          }
        },
      })
    ),
  };
}

export default function MarkdocComponent(props) {
  // Only execute HMR code in development
  return renderers.react(props.markdoc.content, React, {
    components: {
      ...components,
      // Allows users to override default components at runtime, via their _app
      ...props.components,
    },
  });
}
